/* @flow */

import glamorous from 'glamorous';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import numeral from 'numeral';
import t from 'app/utils/translate';
import Checkbox from '../commons/Checkbox';
import CheckButton from '../commons/CheckButton';
import Fieldset from '../commons/Fieldset';
import RangeInput from '../commons/RangeInput';
import ResetButton from '../commons/ResetButton';
import useSheet from '../../jss';
import { FREE } from '../../constants/challengeCategoryTypes';
import { SINGLE, MULTIPLE } from '../../constants/complexityTypes';

type Props = {
  activeCategory?: string,
  classes: Object,
  complexity?: Array<string>,
  currencySymbol: string,
  displayComplexityFilter?: boolean,
  displayEntrantsFilter?: boolean,
  displayFeeFilter?: boolean,
  displayPayoutFilter?: boolean,
  displaySizeFilter?: boolean,
  entrantsOptions?: Array<?number>,
  feeOptions?: Array<number>,
  groupByDate?: boolean,
  lineUpDisabled?: boolean,
  lineUpSizes?: Array<Object>,
  maxEntrants?: number,
  maxFee?: number,
  minEntrants?: number,
  minFee?: number,
  onComplexityChange: (val?: string) => void,
  onGroupByDateChange: (val?: boolean) => void,
  onMaxEntrantsChange: (val?: number) => void,
  onMaxFeeChange: (val?: number) => void,
  onMinEntrantsChange: (val?: number) => void,
  onMinFeeChange: (val?: number) => void,
  onReset: () => void,
  payouts?: Array<Object>,
  scheme: 'light' | 'dark',
};

const defaultProps = {
  activeCategory: null,
  complexity: [],
  displayComplexityFilter: false,
  displayEntrantsFilter: false,
  displayFeeFilter: false,
  displayPayoutFilter: false,
  displaySizeFilter: false,
  entrantsOptions: [],
  feeOptions: [],
  groupByDate: false,
  lineUpDisabled: false,
  lineUpSizes: [],
  maxEntrants: null,
  maxFee: 10000,
  minEntrants: 2,
  minFee: 0,
  payouts: [],
};

const Wrapper = glamorous.div(({ darkMode, theme }) => ({
  alignItems: 'flex-start',
  display: 'flex',
  paddingTop: 7,
  paddingBottom: 7,
  backgroundColor: darkMode ? theme.color.basis1 : theme.color.invertableBasis1,
  color: darkMode ? theme.color.basis5 : theme.color.invertableBasis5,
}));

function invertScheme(scheme) {
  return scheme === 'light' ? 'dark' : 'light';
}

class ChallengesFilterBarContent extends Component<Props> {
  props: Props;

  render() {
    const {
      activeCategory,
      classes,
      complexity,
      currencySymbol,
      displayComplexityFilter,
      displayEntrantsFilter,
      displayFeeFilter,
      displayPayoutFilter,
      displaySizeFilter,
      entrantsOptions,
      feeOptions,
      groupByDate,
      lineUpDisabled,
      lineUpSizes,
      maxEntrants,
      maxFee,
      minEntrants,
      minFee,
      onComplexityChange,
      onGroupByDateChange,
      onMaxEntrantsChange,
      onMaxFeeChange,
      onMinEntrantsChange,
      onMinFeeChange,
      onReset,
      payouts = defaultProps.payouts,
      scheme,
    } = this.props;
    return (
      <Wrapper>
        <div className={classes.contentInner}>
          <div className={classes.responsiveSet}>
            <div className={classes.responsiveSetItem}>
              {displayComplexityFilter && (
                <Fieldset
                  legend={t('Challenge Games')}
                  itemClassName={classes.checkbox}
                  scheme={scheme}
                >
                  <Checkbox
                    checked={complexity.includes(SINGLE)}
                    onChange={() => onComplexityChange(SINGLE)}
                  >
                    1
                  </Checkbox>
                  <Checkbox
                    checked={complexity.includes(MULTIPLE)}
                    onChange={() => onComplexityChange(MULTIPLE)}
                  >
                    2+
                  </Checkbox>
                </Fieldset>
              )}
              <Fieldset legend={t('Grouping')} itemClassName={classes.checkbox} scheme={scheme}>
                <Checkbox checked={groupByDate} onChange={onGroupByDateChange}>
                  {t('Group By Date')}
                </Checkbox>
              </Fieldset>
            </div>
            {(displayEntrantsFilter || displayFeeFilter) && (
              <div className={classes.responsiveSetItem}>
                {displayFeeFilter && (
                  <RangeInput
                    currencySymbol={currencySymbol}
                    format={x => numeral(x).format('0,0.[00]')}
                    label={t('Entry Cost')}
                    lowerValue={minFee}
                    upperValue={maxFee}
                    options={feeOptions}
                    onLowerChange={onMinFeeChange}
                    onUpperChange={onMaxFeeChange}
                    disabled={activeCategory === FREE}
                    nullAtExtremes
                    scheme={scheme}
                  />
                )}
                {displayEntrantsFilter && (
                  <RangeInput
                    label={t('Max. Entries')}
                    lowerValue={minEntrants}
                    upperValue={maxEntrants}
                    options={entrantsOptions}
                    nilString="∞"
                    onLowerChange={onMinEntrantsChange}
                    onUpperChange={onMaxEntrantsChange}
                    nullAtExtremes
                    scheme={scheme}
                  />
                )}
              </div>
            )}
            {displayPayoutFilter && (
              <div className={classes.responsiveSetItem}>
                <Fieldset legend={t('Payout')} itemClassName={classes.payoutButton} scheme={scheme}>
                  {payouts.map(payoutType => (
                    <CheckButton
                      key={payoutType.id}
                      checked={payoutType.checked}
                      onChange={payoutType.onChange}
                      scheme={scheme}
                    >
                      {payoutType.label}
                    </CheckButton>
                  ))}
                </Fieldset>
              </div>
            )}
            {displaySizeFilter && (
              <div className={classes.responsiveSetItem}>
                <div className={classes.full}>
                  <Fieldset
                    legend={t('Lineup Size')}
                    itemClassName={classes.lineupButton}
                    disabled={lineUpDisabled}
                    scheme={scheme}
                  >
                    {lineUpSizes.map(sizeType => (
                      <CheckButton
                        key={sizeType.id}
                        checked={sizeType.checked}
                        onChange={sizeType.onChange}
                        scheme={scheme}
                      >
                        {sizeType.label}
                      </CheckButton>
                    ))}
                  </Fieldset>
                </div>
              </div>
            )}
          </div>
          <div className={classes.final}>
            <ResetButton
              className={classes.reset}
              label={t('Reset Filters')}
              onClick={onReset}
              scheme={invertScheme(scheme)}
            />
          </div>
        </div>
      </Wrapper>
    );
  }
}

ChallengesFilterBarContent.defaultProps = defaultProps;

function applyStyles(theme: Theme) {
  return {
    contentInner: {
      flexGrow: 1,
      paddingBottom: 24,
      position: 'relative',
    },
    checkbox: {
      flexGrow: 1,
      flexShrink: 0,
    },
    full: {
      display: 'flex',
      flexBasis: '100%',
    },
    final: {
      bottom: 0,
      position: 'absolute',
      right: 12,
    },
    reset: {
      marginLeft: 'auto',
    },
    responsiveSet: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    responsiveSetItem: {
      display: 'flex',
      flexWrap: 'wrap',
      paddingLeft: 10,
      paddingRight: 10,
    },
    [theme.mediaQuery.tabletLandscapeAndUp]: {
      contentInner: {
        paddingBottom: 5,
      },
      responsiveSet: {
        flexDirection: 'row',
        flexWrap: 'wrap',
      },
      responsiveSetItem: {
        flexBasis: '25%',
        flexGrow: 1,
        flexShrink: 0,
      },
    },
    [theme.mediaQuery.desktopAndUp]: {
      content: {
        paddingLeft: 5,
        paddingRight: 5,
      },
    },
  };
}

function mapStateToProps({ settings }) {
  return {
    scheme: settings.filters.theme,
    currencySymbol: settings.currency.symbol,
  };
}

export default useSheet(applyStyles)(connect(mapStateToProps)(ChallengesFilterBarContent));
